import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureSettings } from '@/hooks/use-feature-settings';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { DemoModeBadge } from '@/components/ui/demo-mode-badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import {
  Calendar as CalendarIcon,
  List,
  Search,
  Plus,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Clock,
  Users,
  MoreVertical,
  Edit,
  Trash2,
  Repeat,
  UserPlus,
  Eye,
  Package,
  AlertCircle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PriorityIndicator } from '@/components/audits/PriorityIndicator';
import { UserAvatar } from '@/components/audits/UserAvatar';
import { auditDataService } from '@/services/audit-data-service';
import type { Audit, AuditFilters } from '@/types/asset-audit';
import { AuditType, AuditPriority, AuditStatus } from '@/types/asset-audit';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { Checkbox } from '@/components/ui/checkbox';
import { useTeams } from '@/hooks/use-teams';
import { useUsers } from '@/hooks/use-users';
import { getIconByName } from '@/lib/team-icons';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type ViewMode = 'calendar' | 'list';

// Helper function to get color for audit type - extracted to reduce cognitive complexity (S3776)
function getAuditTypeColorValue(type: AuditType): string {
  switch (type) {
    case AuditType.FullInventory:
      return 'rgb(59 130 246)'; // Blue
    case AuditType.SpotCheck:
      return 'rgb(168 85 247)'; // Purple
    case AuditType.Verification:
      return 'rgb(34 197 94)'; // Green
    case AuditType.Compliance:
    case AuditType.ComplianceCheck:
      return 'rgb(249 115 22)'; // Orange
    case AuditType.LocationVerification:
      return 'rgb(236 72 153)'; // Pink
    case AuditType.ConditionAssessment:
    case AuditType.Condition:
      return 'rgb(14 165 233)'; // Sky blue
    case AuditType.HighValueAssets:
      return 'rgb(234 179 8)'; // Yellow
    default:
      return 'rgb(148 163 184)'; // Slate gray
  }
}

// Helper function to check if a user belongs to a team
function isUserInTeam(user: { teams?: Array<string | { teamId: string }> }, teamId: string): boolean {
  if (!Array.isArray(user.teams)) return false;
  return user.teams.some(t => typeof t === 'string' ? t === teamId : t.teamId === teamId);
}

// Helper function to get users belonging to a specific team
function getTeamMembers<T extends { id: string; teams?: Array<string | { teamId: string }> }>(
  users: T[],
  teamId: string
): T[] {
  return users.filter(user => isUserInTeam(user, teamId));
}

// Helper function to get assigned users display text - extracted to fix nested ternary (S3358)
function getAssignedUsersDisplayText(assignedUsers?: Array<{ userName: string }>): string {
  if (!assignedUsers || assignedUsers.length === 0) {
    return 'Unassigned';
  }
  if (assignedUsers.length === 1) {
    return assignedUsers[0].userName;
  }
  return `${assignedUsers[0].userName} +${assignedUsers.length - 1}`;
}

export default function ScheduledAudits(){
  const navigate = useNavigate();
  const { modules } = useFeatureSettings();
  const { toast } = useToast();
  const auditsModule = modules?.audits;
  const teamsHook = useTeams();
  const usersHook = useUsers();
  
  // State
  const [audits, setAudits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Filters
  const [filters, setFilters] = useState<AuditFilters>({
    types: undefined,
    priorities: undefined,
    assignedToUserIds: undefined,
    recurringOnly: false,
  });

  // Dialog states
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);
  const [reassignUsers, setReassignUsers] = useState<string[]>([]);
  const [rescheduleDate, setRescheduleDate] = useState<Date | undefined>(undefined);
  const [deleteReason, setDeleteReason] = useState('');
  
  // Reassign dialog state
  const [selectionMode, setSelectionMode] = useState<'teams' | 'users'>('users');
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);

  // Load audits with scheduled dates (for calendar view, load only current month)
  const loadAudits = async () => {
    setLoading(true);
    try {
      // For calendar view, only load audits for the current month being viewed
      // This optimizes performance for large datasets
      let startDate: string | undefined;
      let endDate: string | undefined;
      
      if (viewMode === 'calendar') {
        // Get first and last day of the current month being viewed
        const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
        startDate = firstDay.toISOString();
        endDate = lastDay.toISOString();
      }
      
      // Get all audits that have a scheduled start date (regardless of status)
      // This includes scheduled, draft, and in-progress audits with future dates
      const result = await auditDataService.getAudits({
        search: searchQuery,
        types: filters.types,
        priorities: filters.priorities,
        pageSize: 1000, // Load more for list view
      });
      
      // Filter audits that have a scheduledStartDate or startDate
      let filteredAudits = (result.items ?? []).filter(audit => 
        audit.scheduledStartDate || audit.startDate
      );
      
      // Apply recurring filter if set
      if (filters.recurringOnly) {
        filteredAudits = filteredAudits.filter(audit => audit.isRecurring);
      }
      
      // For calendar view, filter by month
      if (viewMode === 'calendar' && startDate && endDate) {
        const monthStart = new Date(startDate);
        const monthEnd = new Date(endDate);
        
        filteredAudits = filteredAudits.filter(audit => {
          const auditDate = new Date(audit.scheduledStartDate ?? audit.startDate ?? '');
          return auditDate >= monthStart && auditDate <= monthEnd;
        });
      }
      
      setAudits(filteredAudits);
    } catch (error) {
      console.error('Failed to load scheduled audits:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAudits();
  }, [filters, searchQuery, currentDate, viewMode]);

  // Calendar helpers
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getAuditsForDate = (date: Date) => {
    return audits.filter(audit => {
      if (!audit.scheduledStartDate) return false;
      const auditDate = new Date(audit.scheduledStartDate);
      return (
        auditDate.getDate() === date.getDate() &&
        auditDate.getMonth() === date.getMonth() &&
        auditDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  // Get color for audit type - uses extracted helper function to reduce cognitive complexity (S3776)
  const getAuditTypeColor = (type: AuditType) => getAuditTypeColorValue(type);

  // Handlers
  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleReassign = async (audit: Audit) => {
    setSelectedAudit(audit);
    setReassignUsers(audit.assignedUsers?.map(u => u.userId) ?? []);
    setSelectionMode('users');
    setSelectedTeamIds([]);
    
    // Load teams and users
    await Promise.all([
      teamsHook.loadTeams(),
      usersHook.loadUsers(),
    ]);
    
    setReassignDialogOpen(true);
  };

  const handleReassignSubmit = async () => {
    if (!selectedAudit || reassignUsers.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one user or team.',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      // Update audit with new assignees
      await auditDataService.updateAudit(selectedAudit.id, {
        assignedUsers: reassignUsers.map(userId => {
          const user = usersHook.users.find(u => u.id === userId);
          return {
              userId,
              userName: user?.name ?? `User ${userId}`,
              userEmail: user?.email ?? `user${userId}@example.com`,
            assignedDate: new Date().toISOString(),
            status: 'Pending' as any,
            completedAssets: 0,
            notificationSent: false,
          };
        }),
      });
      
      toast({
        title: 'Audit Reassigned',
        description: `Successfully reassigned audit to ${reassignUsers.length} user(s).`,
      });
      setReassignDialogOpen(false);
      setSelectionMode('users');
      setSelectedTeamIds([]);
      loadAudits();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reassign audit. Please try again.',
        variant: 'destructive',
      });
    }
  };
  
  const toggleUser = (userId: string) => {
    const updated = reassignUsers.includes(userId)
      ? reassignUsers.filter(id => id !== userId)
      : [...reassignUsers, userId];
    setReassignUsers(updated);
  };

  const toggleTeam = (teamId: string) => {
    const updated = selectedTeamIds.includes(teamId)
      ? selectedTeamIds.filter(id => id !== teamId)
      : [...selectedTeamIds, teamId];
    setSelectedTeamIds(updated);
    
    // Update reassignUsers based on selected teams
    const teamUserIds = new Set<string>();
    updated.forEach(tId => {
      const teamUsers = getTeamMembers(usersHook.users, tId);
      teamUsers.forEach(user => teamUserIds.add(user.id));
    });
    
    setReassignUsers(Array.from(teamUserIds));
  };

  const selectAllUsers = () => {
    if (selectionMode === 'users') {
      setReassignUsers(usersHook.users.map(u => u.id));
    } else {
      const allTeamIds = teamsHook.teams.map(t => t.id);
      setSelectedTeamIds(allTeamIds);
      
      // Get all users from all teams
      const allUserIds = new Set<string>();
      usersHook.users.forEach(user => {
        if (Array.isArray(user.teams) && user.teams.length > 0) {
          allUserIds.add(user.id);
        }
      });
      setReassignUsers(Array.from(allUserIds));
    }
  };

  const clearAllUsers = () => {
    setReassignUsers([]);
    setSelectedTeamIds([]);
  };

  const handleModeChange = (mode: 'teams' | 'users') => {
    setSelectionMode(mode);
    clearAllUsers();
  };

  const handleReschedule = (audit: Audit) => {
    setSelectedAudit(audit);
    setRescheduleDate(audit.scheduledStartDate ? new Date(audit.scheduledStartDate) : undefined);
    setRescheduleDialogOpen(true);
  };

  const handleRescheduleSubmit = async () => {
    if (!selectedAudit || !rescheduleDate) return;
    
    try {
      // Update audit with new schedule
      await auditDataService.updateAudit(selectedAudit.id, {
        scheduledStartDate: rescheduleDate.toISOString(),
      });
      
      toast({
        title: 'Audit Rescheduled',
        description: `Successfully rescheduled audit to ${format(rescheduleDate, 'PPP')}.`,
      });
      setRescheduleDialogOpen(false);
      loadAudits();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reschedule audit. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (auditId: string) => {
    // Navigate to audit detail page (edit functionality can be added there)
    navigate(`/audits/${auditId}`);
  };

  const handleDelete = (audit: Audit) => {
    // Validate that audit can be deleted
    if (audit.status === AuditStatus.InProgress) {
      toast({
        title: 'Cannot Delete Active Audit',
        description: 'This audit is currently in progress. Please cancel or complete it before deleting.',
        variant: 'destructive',
      });
      return;
    }
    
    setSelectedAudit(audit);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteSubmit = async () => {
    if (!selectedAudit) return;
    
    // Double-check status before deleting
    if (selectedAudit.status === AuditStatus.InProgress) {
      toast({
        title: 'Cannot Delete Active Audit',
        description: 'This audit is currently in progress. Please cancel or complete it before deleting.',
        variant: 'destructive',
      });
      setDeleteDialogOpen(false);
      return;
    }
    
    try {
      // Delete the audit
      await auditDataService.deleteAudit(selectedAudit.id);
      
      toast({
        title: 'Audit Deleted',
        description: 'The audit has been successfully deleted.',
      });
      setDeleteDialogOpen(false);
      loadAudits();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete audit. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Group audits by week for list view
  const groupAuditsByWeek = () => {
    const grouped: { [key: string]: Audit[] } = {};
    
    audits.forEach(audit => {
      if (!audit.scheduledStartDate) return;
      const date = new Date(audit.scheduledStartDate);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!grouped[weekKey]) {
        grouped[weekKey] = [];
      }
      grouped[weekKey].push(audit);
    });
    
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  };

  const groupedAudits = groupAuditsByWeek();

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CalendarIcon className="h-8 w-8 text-primary" />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight">Scheduled Audits</h1>
                {auditsModule?.mode === 'demo' && <DemoModeBadge />}
              </div>
              <p className="text-muted-foreground text-sm mt-0.5">
                {audits.length} audit{audits.length !== 1 ? 's' : ''} with scheduled dates
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={loadAudits}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => navigate('/audits/create')}>
              <Plus className="h-4 w-4 mr-2" />
              Create Audit
            </Button>
          </div>
        </div>
      </div>

      {/* Content - Split Layout */}
      <div className="flex-1 flex overflow-hidden bg-background">
        {/* Left Sidebar - Filters */}
        <div className="w-80 border-r bg-card overflow-y-auto">
          <div className="p-4 space-y-4">
            {/* View Mode */}
            <div>
              <span id="view-mode-label" className="text-sm font-semibold mb-2 block">View Mode</span>
              <div className="flex items-center border rounded-md" role="group" aria-labelledby="view-mode-label">
                <Button
                  variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('calendar')}
                  className="rounded-r-none flex-1"
                >
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  Calendar
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="rounded-l-none flex-1"
                >
                  <List className="h-4 w-4 mr-2" />
                  List
                </Button>
              </div>
            </div>

            {/* Search */}
            <div>
              <label htmlFor="audit-search" className="text-sm font-semibold mb-2 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="audit-search"
                  placeholder="Search scheduled audits..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Type Filter */}
            <div>
              <label htmlFor="audit-type-filter" className="text-sm font-semibold mb-2 block">Audit Type</label>
              <Select
                value={filters.types?.[0] ?? 'all'}
                onValueChange={(value: string) =>
                  setFilters(prev => ({
                    ...prev,
                    types: value === 'all' ? undefined : [value as AuditType],
                  }))
                }
              >
                <SelectTrigger id="audit-type-filter" className="w-full">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value={AuditType.FullInventory}>Full Inventory</SelectItem>
                  <SelectItem value={AuditType.SpotCheck}>Spot Check</SelectItem>
                  <SelectItem value={AuditType.Verification}>Verification</SelectItem>
                  <SelectItem value={AuditType.Compliance}>Compliance</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Priority Filter */}
            <div>
              <label htmlFor="audit-priority-filter" className="text-sm font-semibold mb-2 block">Priority</label>
              <Select
                value={filters.priorities?.[0] ?? 'all'}
                onValueChange={(value: string) =>
                  setFilters(prev => ({
                    ...prev,
                    priorities: value === 'all' ? undefined : [value as AuditPriority],
                  }))
                }
              >
                <SelectTrigger id="audit-priority-filter" className="w-full">
                  <SelectValue placeholder="All Priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value={AuditPriority.Critical}>Critical</SelectItem>
                  <SelectItem value={AuditPriority.High}>High</SelectItem>
                  <SelectItem value={AuditPriority.Medium}>Medium</SelectItem>
                  <SelectItem value={AuditPriority.Low}>Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Recurring Toggle */}
            <div>
              <span id="recurring-label" className="text-sm font-semibold mb-2 block">Recurring</span>
              <Button
                variant={filters.recurringOnly ? 'default' : 'outline'}
                onClick={() => setFilters(prev => ({ ...prev, recurringOnly: !prev.recurringOnly }))}
                className="w-full"
                aria-labelledby="recurring-label"
              >
                <Repeat className="h-4 w-4 mr-2" />
                {filters.recurringOnly ? 'Recurring Only' : 'All Audits'}
              </Button>
            </div>

            {/* Clear Filters */}
            {(searchQuery || filters.types || filters.priorities || filters.recurringOnly) && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setFilters({ recurringOnly: false });
                }}
                className="w-full"
              >
                Clear Filters
              </Button>
            )}

            {/* Audit Type Legend */}
            {viewMode === 'calendar' && (
              <div className="pt-4 border-t">
                <label className="text-sm font-semibold mb-3 block">Audit Type Legend</label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgb(59 130 246)' }} />
                    <span className="text-xs">Full Inventory</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgb(168 85 247)' }} />
                    <span className="text-xs">Spot Check</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgb(34 197 94)' }} />
                    <span className="text-xs">Verification</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgb(249 115 22)' }} />
                    <span className="text-xs">Compliance</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgb(236 72 153)' }} />
                    <span className="text-xs">Location Verification</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgb(14 165 233)' }} />
                    <span className="text-xs">Condition Assessment</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgb(234 179 8)' }} />
                    <span className="text-xs">High Value Assets</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgb(148 163 184)' }} />
                    <span className="text-xs">Other</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Content Area */}
        <div className="flex-1 overflow-auto p-6">
        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Loading scheduled audits...</p>
            </div>
          </div>
        )}
        {!loading && audits.length === 0 && viewMode === 'list' && (
          <div className="flex flex-col items-center justify-center h-64 gap-4">
            <CalendarIcon className="h-16 w-16 text-muted-foreground" />
            <h3 className="text-xl font-semibold">No Audits Found</h3>
            <p className="text-muted-foreground text-center max-w-md">
              {searchQuery || filters.types || filters.priorities || filters.recurringOnly
                ? 'No audits match your current filters. Try adjusting your search criteria.'
                : 'There are no audits with scheduled dates. Create a new audit to get started.'}
            </p>
          </div>
        )}
        {!loading && (viewMode === 'list' ? audits.length > 0 : true) && (
          <>
            {/* Calendar View */}
            {viewMode === 'calendar' && (
              <div className="max-w-7xl mx-auto">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">{monthName}</h2>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleToday}>
                      Today
                    </Button>
                    <Button variant="outline" size="icon" onClick={handlePreviousMonth}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={handleNextMonth}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Calendar Grid */}
                <div className="border rounded-lg overflow-hidden">
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 bg-muted">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="p-3 text-center text-sm font-medium border-r last:border-r-0">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Days */}
                  <div className="grid grid-cols-7">
                    {/* Empty cells for days before month starts */}
                    {Array.from({ length: startingDayOfWeek }).map((_, i) => (
                      <div key={`empty-${i}`} className="min-h-[120px] border-r border-b bg-muted/30" />
                    ))}

                    {/* Days of the month */}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const day = i + 1;
                      const date = new Date(year, month, day);
                      const dayAudits = getAuditsForDate(date);
                      const isToday =
                        date.getDate() === new Date().getDate() &&
                        date.getMonth() === new Date().getMonth() &&
                        date.getFullYear() === new Date().getFullYear();

                      return (
                        <div
                          key={day}
                          className={`min-h-[120px] border-r border-b p-2 ${
                            isToday ? 'bg-primary/5' : ''
                          }`}
                        >
                          <div className={`text-sm font-medium mb-2 ${isToday ? 'text-primary' : ''}`}>
                            {day}
                          </div>
                          <div className="space-y-1">
                            {dayAudits.slice(0, 3).map(audit => (
                              <button
                                type="button"
                                key={audit.id}
                                className="text-xs p-1.5 rounded cursor-pointer hover:shadow-sm transition-shadow w-full text-left bg-transparent border-none"
                                style={{
                                  backgroundColor: `${getAuditTypeColor(audit.type)} / 0.15`,
                                  borderLeft: `3px solid ${getAuditTypeColor(audit.type)}`,
                                }}
                                onClick={() => navigate(`/audits/${audit.id}`)}
                              >
                                <div className="flex items-center gap-1 mb-0.5">
                                  <div className="font-medium truncate flex-1">{audit.title}</div>
                                  {audit.isRecurring && <Repeat className="h-3 w-3 flex-shrink-0" />}
                                </div>
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <Users className="h-3 w-3 flex-shrink-0" />
                                  <span className="truncate">
                                    {getAssignedUsersDisplayText(audit.assignedUsers)}
                                  </span>
                                </div>
                              </button>
                            ))}
                            {dayAudits.length > 3 && (
                              <div className="text-xs text-muted-foreground text-center py-1">
                                +{dayAudits.length - 3} more
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <div className="max-w-7xl mx-auto space-y-8">
                {groupedAudits.map(([weekStart, weekAudits]) => {
                  const startDate = new Date(weekStart);
                  const endDate = new Date(startDate);
                  endDate.setDate(endDate.getDate() + 6);
                  
                  return (
                    <div key={weekStart}>
                      <h3 className="text-lg font-semibold mb-4">
                        {startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} -{' '}
                        {endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </h3>
                      <div className="space-y-3">
                        {weekAudits.map(audit => (
                          <Card
                            key={audit.id}
                            className="border border-border hover:shadow-md transition-all duration-200"
                            style={{
                              borderLeftWidth: '4px',
                              borderLeftColor: getAuditTypeColor(audit.type),
                            }}
                          >
                            <CardContent className="p-3">
                              <div className="flex items-center gap-3">
                                {/* Date Badge */}
                                <div 
                                  className="flex-shrink-0 text-center px-3 py-2 rounded-lg"
                                  style={{
                                    backgroundColor: `${getAuditTypeColor(audit.type)}15`,
                                  }}
                                >
                                  <div className="text-2xl font-bold" style={{ color: getAuditTypeColor(audit.type) }}>
                                    {audit.scheduledStartDate ? new Date(audit.scheduledStartDate).getDate() : '-'}
                                  </div>
                                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    {audit.scheduledStartDate ? new Date(audit.scheduledStartDate).toLocaleDateString('en-US', {
                                      month: 'short',
                                    }) : '-'}
                                  </div>
                                </div>

                                {/* Main Content */}
                                <div className="flex-1 min-w-0">
                                  {/* Title Row */}
                                  <div className="flex items-center gap-2 mb-1">
                                    <h3 className="font-semibold text-base truncate">{audit.title}</h3>
                                    {audit.isRecurring && (
                                      <Repeat className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                    )}
                                  </div>

                                  {/* Description */}
                                  {audit.description && (
                                    <p className="text-sm text-muted-foreground mb-2 line-clamp-1">
                                      {audit.description}
                                    </p>
                                  )}

                                  {/* Info Row */}
                                  <div className="flex items-center gap-4 text-sm flex-wrap">
                                    {/* Type Badge */}
                                    <Badge 
                                      variant="secondary" 
                                      className="font-medium text-xs"
                                      style={{
                                        backgroundColor: `${getAuditTypeColor(audit.type)}20`,
                                        color: getAuditTypeColor(audit.type),
                                        borderColor: `${getAuditTypeColor(audit.type)}40`,
                                      }}
                                    >
                                      {audit.type.replace(/([A-Z])/g, ' $1').trim()}
                                    </Badge>

                                    {/* Priority */}
                                    <PriorityIndicator priority={audit.priority} size="sm" />

                                    {/* Assigned Users */}
                                    <div className="flex items-center gap-1.5">
                                      <Users className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                      {(audit.assignedUsers?.length ?? 0) > 0 ? (
                                        <span className="text-xs">
                                          {audit.assignedUsers![0].userName}
                                          {(audit.assignedUsers?.length ?? 0) > 1 && (
                                            <span className="text-muted-foreground">
                                              {' '}+{(audit.assignedUsers?.length ?? 0) - 1}
                                            </span>
                                          )}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-muted-foreground italic">Unassigned</span>
                                      )}
                                    </div>

                                    {/* Assets Count */}
                                    {audit.totalAssets > 0 && (
                                      <div className="flex items-center gap-1">
                                        <Package className="h-3.5 w-3.5 text-muted-foreground" />
                                        <span className="text-xs font-medium">{audit.totalAssets}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => navigate(`/audits/${audit.id}`)}
                                    className="gap-1.5 h-8"
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                    View
                                  </Button>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8">
                                        <MoreVertical className="h-3.5 w-3.5" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem
                                        onClick={() => handleReassign(audit)}
                                      >
                                        <UserPlus className="h-4 w-4 mr-2" />
                                        Reassign
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={() => handleReschedule(audit)}
                                      >
                                        <Clock className="h-4 w-4 mr-2" />
                                        Reschedule
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={() => handleEdit(audit.id)}
                                      >
                                        <Edit className="h-4 w-4 mr-2" />
                                        Edit
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={() => handleDelete(audit)}
                                        className="text-destructive"
                                      >
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        Delete
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
        </div>
      </div>

      {/* Reassign Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reassign Audit</DialogTitle>
            <DialogDescription>
              Select teams or individual users to assign to "{selectedAudit?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Selection Mode Toggle */}
            <div className="space-y-3">
              <Label className="font-semibold">Selection Mode</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={selectionMode === 'teams' ? 'default' : 'outline'}
                  onClick={() => handleModeChange('teams')}
                  className="flex-1"
                  size="sm"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Select by Team
                </Button>
                <Button
                  type="button"
                  variant={selectionMode === 'users' ? 'default' : 'outline'}
                  onClick={() => handleModeChange('users')}
                  className="flex-1"
                  size="sm"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Select Individual Users
                </Button>
              </div>
            </div>

            {/* Team/User Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">
                  {selectionMode === 'teams' ? 'Select Teams' : 'Select Users'} <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAllUsers}
                    className="text-xs text-primary hover:underline"
                  >
                    Select All
                  </button>
                  <span className="text-xs text-muted-foreground">|</span>
                  <button
                    type="button"
                    onClick={clearAllUsers}
                    className="text-xs text-muted-foreground hover:underline"
                  >
                    Clear All
                  </button>
                </div>
              </div>

              {selectionMode === 'teams' ? (
                // Team Selection
                <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                  {teamsHook.teams.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <AlertCircle className="h-10 w-10 text-muted-foreground mb-2" />
                      <p className="text-sm font-medium text-muted-foreground">No teams available</p>
                    </div>
                  ) : (
                    teamsHook.teams.map((team) => {
                      const isSelected = selectedTeamIds.includes(team.id);
                      const teamMemberCount = getTeamMembers(usersHook.users, team.id).length;
                      const IconComponent = getIconByName(team.icon);
                      
                      return (
                        <div
                          key={team.id}
                          className="flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors"
                        >
                          <Checkbox
                            id={`team-${team.id}`}
                            checked={isSelected}
                            onCheckedChange={() => toggleTeam(team.id)}
                          />
                          <label
                            htmlFor={`team-${team.id}`}
                            className="flex items-center gap-3 flex-1 cursor-pointer"
                          >
                            <div 
                              className="w-8 h-8 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: team.color }}
                            >
                              {IconComponent ? (
                                <IconComponent className="h-4 w-4 text-white" />
                              ) : (
                                <Users className="h-4 w-4 text-white" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium">{team.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {teamMemberCount} {teamMemberCount === 1 ? 'member' : 'members'}
                              </p>
                            </div>
                          </label>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                // User Selection
                <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                  {usersHook.users.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <AlertCircle className="h-10 w-10 text-muted-foreground mb-2" />
                      <p className="text-sm font-medium text-muted-foreground">No users available</p>
                    </div>
                  ) : (
                    usersHook.users.map((user) => {
                      const isSelected = reassignUsers.includes(user.id);
                      return (
                        <div
                          key={user.id}
                          className="flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors"
                        >
                          <Checkbox
                            id={`user-${user.id}`}
                            checked={isSelected}
                            onCheckedChange={() => toggleUser(user.id)}
                          />
                          <label
                            htmlFor={`user-${user.id}`}
                            className="flex items-center gap-3 flex-1 cursor-pointer"
                          >
                            <UserAvatar user={{ id: user.id, name: user.name, email: user.email }} size="sm" />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{user.name}</p>
                              <p className="text-xs text-muted-foreground">{user.email}</p>
                            </div>
                          </label>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Selection Summary */}
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">
                    {selectionMode === 'teams' ? 'Selected Teams & Members' : 'Selected Users'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {reassignUsers.length} {reassignUsers.length === 1 ? 'user' : 'users'} selected
                  </p>
                </div>
                <Badge variant="secondary" className="text-lg px-3 py-1">
                  {reassignUsers.length}
                </Badge>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Currently assigned: {selectedAudit?.assignedUsers?.map(u => u.userName).join(', ') ?? 'None'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReassignSubmit} disabled={reassignUsers.length === 0}>
              Reassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Reschedule Audit</DialogTitle>
            <DialogDescription>
              Select a new date for "{selectedAudit?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Schedule Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !rescheduleDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {rescheduleDate ? format(rescheduleDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={rescheduleDate}
                    onSelect={setRescheduleDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                Current date: {selectedAudit?.scheduledStartDate ? format(new Date(selectedAudit.scheduledStartDate), 'PPP') : 'Not set'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRescheduleSubmit} disabled={!rescheduleDate}>
              Reschedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Audit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedAudit?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for Deletion (Optional)</Label>
              <Textarea
                value={deleteReason}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDeleteReason(e.target.value)}
                placeholder="Enter a reason for deleting this audit..."
                rows={3}
              />
            </div>
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-destructive font-medium">
                    Warning: This will permanently delete the audit and all associated data.
                  </p>
                  <p className="text-xs text-destructive/80 mt-1">
                    Note: Active audits (in progress) cannot be deleted. Please cancel or complete them first.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSubmit}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Audit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
